import React, { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";

/**
 * Free-text input with a "remembered values" dropdown.
 *
 * Backend endpoints: /api/text-suggestions/{field}
 *   GET (?q=)  → list matching values (sorted by frequency + recency)
 *   POST       → record a value (call on blur)
 *   DELETE     → remove a value from history
 *
 * Props mirror <input>. Extra props:
 *   field          – suggestion bucket key (e.g. "rider", "horse", "sub_label")
 *   value          – controlled string
 *   onChange       – (nextValue) => void   *(receives raw string, not the event)*
 *   onCommit       – optional (value) => void, fired when the user picks a suggestion or blurs.
 *                    Use this if you need to trigger a save immediately (e.g. inline cut edit).
 *   as             – "input" (default) or "textarea"
 */
export function AutocompleteInput({
  field,
  value,
  onChange,
  onCommit,
  as = "input",
  className = "",
  placeholder = "",
  disabled = false,
  autoFocus = false,
  // Optional: pull suggestions from a canonical endpoint instead of the
  // per-field text-suggestions bucket. Used by the Studio Performance
  // picker so it shows the SAME list as the "Move" icon (i.e. real
  // performances from db.events), not just the free-text history.
  //   sourceEndpoint  – e.g. "/events"
  //   sourceMap       – (json) => string[] — how to extract names
  sourceEndpoint = null,
  sourceMap = null,
  "data-testid": testId,
  ...rest
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef(null);
  const lastFetchedRef = useRef("");

  const fetchSuggestions = useCallback(async (q) => {
    if (!field && !sourceEndpoint) return;
    try {
      setLoading(true);
      if (sourceEndpoint) {
        // Canonical list mode — one fetch, filtered client-side. Normalise
        // to { value } shape so the render (which reads s.value) works.
        const res = await api.get(sourceEndpoint);
        const extract = sourceMap || ((d) => (d.events || d.items || []).map((x) => x.name || x));
        const all = (extract(res.data) || []).filter(Boolean);
        const needle = (q || "").trim().toLowerCase();
        // Skip filtering when the input value already matches an item
        // exactly — the user has picked it, so they probably want to
        // browse the full list next time they open the dropdown.
        const exact = all.some((s) => (s || "").toLowerCase() === needle);
        const filtered = (needle && !exact)
          ? all.filter((s) => (s || "").toLowerCase().includes(needle))
          : all;
        setSuggestions(filtered.map((s) => ({ value: s })));
      } else {
        const res = await api.get(`/text-suggestions/${field}`, { params: { q: q || "" } });
        setSuggestions(Array.isArray(res.data) ? res.data : []);
      }
      lastFetchedRef.current = q || "";
    } catch (e) {
      // Silent — dropdown is a convenience, not critical.
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [field, sourceEndpoint, sourceMap]);

  // Debounce fetching as the user types.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value || "");
    }, 150);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, open, fetchSuggestions]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setHighlighted(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const recordValue = useCallback(async (val) => {
    const trimmed = (val || "").trim();
    if (!trimmed || !field) return;
    try {
      await api.post(`/text-suggestions/${field}`, { value: trimmed });
    } catch (e) {
      // best-effort — ignore
    }
  }, [field]);

  const pick = (val) => {
    onChange && onChange(val);
    setOpen(false);
    setHighlighted(-1);
    onCommit && onCommit(val);
    // record so it climbs the frequency list
    recordValue(val);
  };

  const handleFocus = () => {
    setOpen(true);
    // Prime the list immediately with what we have.
    fetchSuggestions(value || "");
  };

  const handleBlur = () => {
    // Delay slightly so click-on-suggestion still fires.
    setTimeout(() => {
      if (!wrapRef.current || !wrapRef.current.contains(document.activeElement)) {
        setOpen(false);
        setHighlighted(-1);
        recordValue(value);
      }
    }, 120);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min((suggestions.length - 1), h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(-1, h - 1));
    } else if (e.key === "Enter") {
      if (highlighted >= 0 && suggestions[highlighted]) {
        e.preventDefault();
        pick(suggestions[highlighted].value);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  const removeSuggestion = async (e, val) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/text-suggestions/${field}`, { params: { value: val } });
    } catch (err) { /* ignore */ }
    setSuggestions((prev) => prev.filter((s) => s.value !== val));
  };

  const commonProps = {
    ref: inputRef,
    value: value ?? "",
    onChange: (e) => onChange && onChange(e.target.value),
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    className,
    placeholder,
    disabled,
    autoFocus,
    autoComplete: "off",
    "data-testid": testId,
    ...rest,
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId ? `${testId}-wrap` : undefined}>
      {as === "textarea" ? <textarea {...commonProps} /> : <input {...commonProps} />}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-stone-950 border border-stone-700 shadow-xl text-sm"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {suggestions.map((s, idx) => (
            <li
              key={s.value}
              onMouseDown={(e) => { e.preventDefault(); pick(s.value); }}
              onMouseEnter={() => setHighlighted(idx)}
              className={`flex items-center justify-between gap-2 px-3 py-1.5 cursor-pointer ${
                idx === highlighted ? "bg-orange-800/40 text-orange-100" : "text-stone-200 hover:bg-stone-800"
              }`}
              data-testid={testId ? `${testId}-suggestion-${idx}` : undefined}
            >
              <span className="truncate">{s.value}</span>
              {!sourceEndpoint && (
                <button
                  type="button"
                  onMouseDown={(e) => removeSuggestion(e, s.value)}
                  className="opacity-40 hover:opacity-100 hover:text-red-400 shrink-0"
                  title="Forget this suggestion"
                  data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
          {loading && (
            <li className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-500">Loading…</li>
          )}
        </ul>
      )}
    </div>
  );
}

export default AutocompleteInput;
